// Notifications dropdown — bell-icon trigger in the TopNav.
//
// v1 MOCK: shows a fixed list of representative notifications so the
// surface area is real for design review. The eventual backend lands
// per cutover-checklist §1 + spec in shared-chrome-v2 §4 (eight
// notification types: mention, mention reply, task assigned, task
// due, data needed, stale record, opportunity stale, rhythm
// touchpoint, automation event).
//
// Dropdown state lives locally in this component (one bell, one
// dropdown — no need for chrome-context coordination). Outside-click
// + Esc dismiss.

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useNavigate } from "react-router-dom";

import {
  AtSign,
  Bot,
  CalendarCheck,
  FileSignature,
  CalendarDays,
  CheckSquare,
  Clock,
  Database,
  FileWarning,
  Inbox,
  Paperclip,
  Share2,
  ShieldAlert,
  Star,
  Zap,
} from "lucide-react";

import { api } from "../api";
import {
  filterNotificationsByTab,
  NOTIFICATION_TABS,
  type MockNotification,
  type NotificationKind,
  type NotificationTab,
} from "./notifications-data";

// Maps each notification kind to a Lucide glyph. Centralized here so
// the visual taxonomy is easy to scan + extend when new kinds land.
// Exported so the /notifications page renders the same taxonomy.
export function NotificationKindIcon({ kind }: { kind: NotificationKind }) {
  const props = { size: 13, strokeWidth: 1.75 } as const;
  switch (kind) {
    case "mention":
      return <AtSign {...props} />;
    case "task_assigned":
    case "task_due":
      return <CheckSquare {...props} />;
    case "data_needed":
      return <Database {...props} />;
    case "stale_record":
      return <Clock {...props} />;
    case "opportunity_stale":
      return <FileWarning {...props} />;
    case "rhythm_touchpoint":
      return <CalendarDays {...props} />;
    case "automation_event":
      return <Zap {...props} />;
    case "form_submission":
      return <Inbox {...props} />;
    case "booking":
      return <CalendarCheck {...props} />;
    case "esign":
      return <FileSignature {...props} />;
    case "document_request":
      return <Paperclip {...props} />;
    case "shared_with_you":
      return <Share2 {...props} />;
    case "system_health":
      return <ShieldAlert {...props} />;
    case "ai_suggestion":
      return <Bot {...props} />;
    default:
      return <Star {...props} />;
  }
}

export function NotificationsDropdown() {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<NotificationTab>("all");
  const [readIds, setReadIds] = useState<Set<string>>(new Set());
  const [notifications, setNotifications] = useState<MockNotification[]>([]);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);
  const dropdownId = useId();

  // Real feed (AI nudges + assigned tasks). Loaded on mount and refreshed
  // whenever the panel OPENS, so the count stays roughly current without
  // polling. Failures degrade to an empty list (the empty state shows).
  // The loadedOnce ref skips the open→false transition — without it the
  // [open] dep refetched on every CLOSE too, a redundant request per
  // dismissal (speed program Phase 6). The ref flips only after a load
  // actually LANDED (not on attempt): under StrictMode's dev-only
  // run→cleanup→run, an attempt-time flip cancelled the first run and
  // skipped the second, leaving the badge empty until first open
  // (pre-promote audit F3 — dev-only, but the guard is free).
  const loadedOnce = useRef(false);
  useEffect(() => {
    if (!open && loadedOnce.current) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = await api.listNotifications();
        if (!cancelled) {
          loadedOnce.current = true;
          setNotifications(items);
          // The server now carries each person's reads, so the list it
          // just returned is the truth. Dropping this tab's local reads
          // also lets a grouped notice that gained a new event since it
          // was read show as unread again.
          setReadIds(new Set());
        }
      } catch {
        if (!cancelled) setNotifications([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  const filtered = useMemo(
    () => filterNotificationsByTab(notifications, tab),
    [notifications, tab],
  );
  // Read = the server remembers it read (survives a reload, Brandon
  // 2026-09-28), or it was read in this tab and the save is in flight.
  const isRead = useCallback(
    (n: MockNotification) => n.read === true || readIds.has(n.id),
    [readIds],
  );
  const unreadCount = useMemo(
    () => notifications.filter((n) => !isRead(n)).length,
    [notifications, isRead],
  );

  useEffect(() => {
    if (!open) return;
    function onMouseDown(event: MouseEvent) {
      const target = event.target as Node | null;
      if (!target) return;
      if (panelRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      setOpen(false);
    }
    function onKey(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const close = useCallback(() => setOpen(false), []);
  const navigate = useNavigate();
  // Show the read at once, then save it. A failed save is logged and the
  // item simply reads as unread again on the next load: nothing is lost,
  // and the badge never claims more than the server knows.
  const saveReads = useCallback((ids: string[]) => {
    if (ids.length === 0) return;
    setReadIds((prev) => {
      const next = new Set(prev);
      for (const id of ids) next.add(id);
      return next;
    });
    api.markNotificationsRead(ids).catch((err: unknown) => {
      console.warn("Could not save notification reads", { count: ids.length, err });
    });
  }, []);
  const markAllRead = useCallback(() => {
    saveReads(notifications.filter((n) => !isRead(n)).map((n) => n.id));
  }, [notifications, isRead, saveReads]);
  // Mark the notification read, then route to its destination and close
  // the panel — previously the click only marked read and went nowhere
  // (QA NEW-08b).
  const handleRowClick = useCallback(
    (notif: MockNotification) => {
      if (!isRead(notif)) saveReads([notif.id]);
      void navigate(notif.href);
      close();
    },
    [navigate, close, isRead, saveReads],
  );

  return (
    <div className="notifications-wrap">
      <button
        ref={triggerRef}
        type="button"
        className="top-nav-icon-btn"
        aria-label={`Notifications${unreadCount > 0 ? ` — ${unreadCount} unread` : ""}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={dropdownId}
        onClick={() => setOpen((v) => !v)}
        data-testid="notifications-bell"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="20"
          height="20"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unreadCount > 0 && (
          <span className="top-nav-icon-badge" aria-hidden="true">
            {unreadCount > 9 ? "9+" : String(unreadCount)}
          </span>
        )}
      </button>
      {open && (
        <div
          ref={panelRef}
          id={dropdownId}
          role="dialog"
          aria-label="Notifications"
          className="notifications-panel"
          data-testid="notifications-panel"
        >
          <div className="notifications-header">
            <h3>Notifications</h3>
            <button
              type="button"
              className="notifications-mark-all"
              onClick={markAllRead}
            >
              Mark all as read
            </button>
          </div>
          <div className="notifications-tabs" role="tablist">
            {NOTIFICATION_TABS.map((t) => {
              const count = filterNotificationsByTab(
                notifications,
                t.value,
              ).length;
              return (
                <button
                  key={t.value}
                  type="button"
                  role="tab"
                  aria-selected={tab === t.value}
                  className={`notifications-tab${tab === t.value ? " is-active" : ""}`}
                  onClick={() => setTab(t.value)}
                  data-testid={`notifications-tab-${t.value}`}
                >
                  {t.label}
                  <span className="notifications-tab-count">{count}</span>
                </button>
              );
            })}
          </div>
          <ul className="notifications-list">
            {filtered.length === 0 && (
              <li className="notifications-empty">All caught up.</li>
            )}
            {filtered.map((n) => {
              const isUnread = !isRead(n);
              return (
                <li
                  key={n.id}
                  className={`notification${isUnread ? " is-unread" : ""}`}
                  data-testid={`notification-${n.id}`}
                >
                  <button
                    type="button"
                    className="notification-row"
                    onClick={() => handleRowClick(n)}
                  >
                    {/* April 2026 design refresh: 6px left rail
                        unread indicator + 26×26 type-tinted icon
                        tile per OVERLAYS.md §3. */}
                    <span className="notification-rail" aria-hidden="true" />
                    <span
                      className={`notification-icon notification-icon--${n.kind}`}
                      aria-hidden="true"
                    >
                      <NotificationKindIcon kind={n.kind} />
                    </span>
                    <span className="notification-body">
                      <span className="notification-title">{n.title}</span>
                      <span className="notification-context">{n.context}</span>
                      <span className="notification-time">{n.timeLabel}</span>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="notifications-footer">
            <Link to="/notifications" onClick={close}>
              See all notifications →
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
